//
//
//
// Client-side Analytics Events
//
//
//

import { mapToRegionEnumLabels, type regionEnum } from "../form/data/form-data-enums";
import { analyticsBrowser } from "./analyticsBrowser.client";

export type TrackEvents = {
  //
  //
  //
  // Misc
  //
  //
  //
  /**
   * TRACK - Error Occurred
   */
  "Error Occurred": {
    actionId: "login-form-submission-failed";
    errorMessageSafe: string;
    errorMessageUnsafe: string;
  };

  /**
   * TRACK - Help Button Clicked
   */
  "Help Button Clicked": {
    location: "form-page" | "home-page";
  };

  //
  //
  // Walkthroughs
  //
  //
  //

  /**
   * TRACK - Walkthrough video dialog opened
   */
  "Walkthrough Video Dialog Opened": {
    videoId: string;
    videoTitle: string;
  };

  //
  //
  //
  // LOGIN
  //
  //
  //

  /**
   * TRACK - Login Form Submitted
   */
  "Login Form Submitted": {
    email: string;
    accessCode: string;
  };

  /**
   * TRACK - Logged out
   */
  "Logged Out": {};

  //
  //
  //
  // WELCOME
  //
  //
  //

  /**
   * TRACK - Consent Status Changed
   */
  "Consent Status Changed": {
    consentId: "ae-consent";
    consentStatus: "given" | "not-given";
    consentCopy: string;
  };

  //
  //
  //
  // HOME/NAV
  //
  //
  //

  /**
   * TRACK - Participants Tab Opened
   */
  "Participants Tab Opened": {
    location: "home" | "nav-sheet";
    phase?: "1" | "2";
  };

  /**
   * TRACK - Touch Point 2 Header Started
   */
  "Touch Point 2 Header Started": {
    headerId: string;
    region: string;
  };

  /**
   * TRACK - Region Tab Switched
   */
  "Region Tab Switched": {
    phase: "1" | "2";
    fromRegion: string;
    toRegion: string;
    location: string;
  };

  /**
   * TRACK - My Responses Viewed
   */
  "My Responses Viewed": {
    phase: "1" | "2";
    region: string;
    headerId?: string;
    sectionId?: string;
  };

  //
  //
  // FORM
  //
  //
  //

  /**
   * TRACK - Form Section Started
   */
  "Form Section Started": {
    sectionId:
      | "initial-presentation"
      | "confirmatory-diagnosis"
      | "treatment-early-stage"
      | "treatment-late-stage"
      | "ongoing-management";
    phase: "1" | "2";
  };

  /**
   * TRACK - Form Page Submitted
   */
  "Form Page Submitted": {
    sectionId:
      | "initial-presentation"
      | "confirmatory-diagnosis"
      | "treatment-early-stage"
      | "treatment-late-stage"
      | "ongoing-management";
    pageNumber: number;
    questionKeys: string[];
    phase: "1" | "2";
  };

  /**
   * TRACK - Form Header Submitted (Phase 2)
   */
  "Form Header Submitted": {
    headerId: string;
    region: string;
    questionKeys: string[];
    phase: "1" | "2";
  };

  /**
   * TRACK - Enlarge Schema Dialog Opened
   */
  "Enlarge Schema Dialog Opened": {
    sectionId: string;
    schemaSrc: string;
    schemaAlt: string;
  };

  //
  //
  //
  // RESPONSES
  //
  //
  //

  /**
   * TRACK - Response Collapsible Opened
   */
  "Response Collapsible Opened": {
    responseKey: string;
    propertyKey?: string;
    responseTitle: string;
  };

  /**
   * TRACK - Response Collapsible Expanded All
   */
  "Response Collapsible Expanded All": {};

  /**
   * TRACK - Expand All Clicked
   */
  "Expand All Clicked": {
    phase: "1" | "2";
    context: string;
  };

  /**
   * TRACK - Collapse All Clicked
   */
  "Collapse All Clicked": {
    phase: "1" | "2";
    context: string;
  };

  /**
   * TRACK - Response Detail Dialog Opened
   */
  "Response Detail Dialog Opened": {
    responseKey: string;
    responseTitle: string;
    authorId: string;
    authorName: string;
    phase: "1" | "2";
  };

  /**
   * TRACK - Comment Posted
   */
  "Comment Posted": {
    responseKey: string;
    responseTitle: string;
    responseAuthorId: string;
    responseAuthorName: string;
    commentId: string;
    commentContent: string;
    commentAuthorId: string;
    commentAuthorName: string;
    phase: "1" | "2";
  };

  /**
   * TRACK - Email Notifications Preference Changed
   */
  "Email Preferences Updated": {
    userId: string;
    emailNotificationsEnabled: boolean;
  };

  //
  //
  //
  // SESSION
  //
  //
  //

  /**
   * TRACK - Session Started
   */
  "Session Started": {
    sessionId: string;
  };

  //
  // NOTE: filter tracking is done with page view tracking
  //
};

/**
 * TRACK - Track an event
 *
 * @description Track an event
 */
export function browserTrackEvent<E extends keyof TrackEvents>(eventName: E, eventProps: TrackEvents[E]) {
  analyticsBrowser.track(eventName, eventProps ?? {});

  if (eventName === "Logged Out") {
    analyticsBrowser.reset();
  }
}

/**
 * IDENTIFY - Identify a user
 *
 * @description Identify a user
 */
export function browserIdentifyEvent(eventProps: {
  userId: string;
  email: string;
  region: (typeof regionEnum)[number];
  fullName?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  practice?: string;
  jobTitle?: string;
}) {
  analyticsBrowser.identify(eventProps.userId, eventProps);
  analyticsBrowser.group(eventProps.region, {
    regionId: eventProps.region,
    regionName: mapToRegionEnumLabels(eventProps.region, (en: string, fr: string) => en),
  });
}

/**
 * PAGE - Page Viewed
 *
 * @description Track when a user views a page
 */
export function browserPageEvent(eventProps?: { language: "en" | "fr" }) {
  analyticsBrowser.page(undefined, undefined, eventProps ?? {});
}
